/**
 * Web push subscription management.
 *
 * Three separate things have to line up before a push can arrive, and they
 * fail in different ways, so they are reported separately rather than as one
 * "notifications broken":
 *
 *   1. A SECURE CONTEXT. Service workers and PushManager only exist on https
 *      or localhost. Served over plain http from an IP address they are simply
 *      not on the object, and no amount of permission granting conjures them.
 *   2. PERMISSION. Granted by the user, from a click, once. A denial is sticky
 *      and cannot be re-prompted from the page.
 *   3. A SUBSCRIPTION registered with us. The browser has one; the server has
 *      to have been told about it, which is what this file is for.
 *
 * The VAPID public key is baked into the bundle at build time. It is public by
 * design - it is what identifies the sender to the push service, and the
 * private half never leaves the sender.
 */
import { supabase, unwrap } from './supabase.js';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

/** Everything a caller needs to explain the state of push on this device. */
export function pushCapability() {
  const secure = typeof window !== 'undefined' && window.isSecureContext;
  const hasWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const hasPush = typeof window !== 'undefined' && 'PushManager' in window;
  const hasNotification = typeof window !== 'undefined' && 'Notification' in window;

  return {
    supported: secure && hasWorker && hasPush && hasNotification,
    secure,
    configured: !!VAPID_PUBLIC_KEY,
    permission: hasNotification ? Notification.permission : 'unsupported',
  };
}

/**
 * The applicationServerKey has to be raw bytes; VAPID keys travel as base64url.
 * atob wants standard base64, hence the substitution and the padding.
 */
function urlBase64ToUint8Array(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

/** The PushSubscription's keys arrive as ArrayBuffers; the table wants text. */
function encodeKey(subscription, name) {
  const key = subscription.getKey(name);
  if (!key) return null;
  return btoa(String.fromCharCode(...new Uint8Array(key)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

let registrationPromise = null;

/** Registers the worker once per page, whatever calls it. */
export function ensureServiceWorker() {
  if (!pushCapability().supported) return Promise.resolve(null);
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => navigator.serviceWorker.ready.then(() => registration))
      .catch((error) => {
        registrationPromise = null;
        throw error;
      });
  }
  return registrationPromise;
}

/**
 * Asks for permission if needed, subscribes, and records the endpoint.
 *
 * Must be called from a user gesture: browsers ignore a permission request
 * that is not, and Safari refuses outright. Returns the stored row, or throws
 * with something worth showing the user.
 */
export async function subscribeToPush(userId) {
  const capability = pushCapability();
  if (!capability.secure) {
    throw new Error('Push needs a secure connection. This works over HTTPS or on localhost, but not over plain HTTP from an IP address.');
  }
  if (!capability.supported) {
    throw new Error('This browser does not support web push.');
  }
  if (!capability.configured) {
    throw new Error('Push is not configured on this deployment: VITE_VAPID_PUBLIC_KEY was not set at build time.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Notifications are blocked for this site. Re-enable them in your browser site settings.'
      : 'Notifications were not enabled.');
  }

  const registration = await ensureServiceWorker();

  // An existing subscription is reused unless it was issued for a different
  // VAPID key - which happens when the deployment's keys are rotated, and
  // leaves a subscription no one can push to.
  let subscription = await registration.pushManager.getSubscription();
  const wantedKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
  if (subscription) {
    const current = new Uint8Array(subscription.options?.applicationServerKey || []);
    const sameKey = current.length === wantedKey.length
      && current.every((byte, index) => byte === wantedKey[index]);
    if (!sameKey) {
      await subscription.unsubscribe();
      subscription = null;
    }
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      // Required by Chrome: every push must result in something the user sees.
      userVisibleOnly: true,
      applicationServerKey: wantedKey,
    });
  }

  // Upserting on the endpoint means re-running this on the same browser
  // refreshes the row rather than piling up duplicates that all resolve to the
  // same device.
  return unwrap(await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: encodeKey(subscription, 'p256dh'),
      auth: encodeKey(subscription, 'auth'),
      user_agent: navigator.userAgent.slice(0, 300),
    }, { onConflict: 'endpoint' })
    .select()
    .single());
}

/**
 * Drops this browser's subscription, at the browser and on the server.
 *
 * Only this device: the master switch in Settings is what turns push off
 * everywhere, and someone unsubscribing their laptop should not silence their
 * phone.
 */
export async function unsubscribeFromPush() {
  if (!pushCapability().supported) return false;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return false;

  // Server first. If the browser drops it and the delete then fails, the row
  // lingers as an endpoint nothing can be delivered to.
  await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint);
  await subscription.unsubscribe();
  return true;
}

/** Is this particular browser registered right now? */
export async function isSubscribedHere() {
  if (!pushCapability().supported) return false;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return !!(await registration?.pushManager.getSubscription());
}
